<template>
  <component
    :is="activeComponent"
    class="tw-media"
    :tag-name="tagName"
    :value="value"
    :style="[style, cssVars]"
    :attr="attr"
    :element="element"
    :is-editor-mode="isEditorMode"
    :sizes="sizes"
  />
</template>

<script lang="ts" setup>
import { type PropType, computed, toRef } from 'vue';
import type { Element as ElementType } from '../../types/model';
import { useSpacing } from '@zotlo-shared/composable/useSpacing';
import { useSizing } from '@zotlo-shared/composable/useSizing';
import Image from './components/image.vue';
import Video from './components/video.vue';
import Embed from './components/embed.vue';

const props = defineProps({
  tagName: { type: String, default: 'img' },
  value: { type: String, default: '' },
  style: { type: Object, default: () => ({}) },
  attr: { type: Object as PropType<ElementType<'image'>['attr']>, default: () => ({}) },
  element: { type: Object as PropType<ElementType<'image'>>, default: () => ({}) },
  isEditorMode: { type: Boolean, default: false },
});

const element = toRef(props, 'element');
const { padding, margin } = useSpacing(element);

const { sizes } = useSizing(element, {
  sizes: {
    desktop: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
    },
    tablet: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      autoHeight: true,
    },
    mobile: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      autoHeight: true,
    },
  },
});

const options = computed(() => props.element.options);

const activeComponent = computed(() => {
  if (options.value.mediaType === 'video') return Video;
  if (/^embed_/.test(options.value.mediaType || '')) return Embed;
  return Image
});

const cssVars = computed(() => {
  return {
    '--media-desktop-margin-top': margin.value?.desktop?.top,
    '--media-desktop-margin-left': margin.value?.desktop?.left,
    '--media-desktop-margin-bottom': margin.value?.desktop?.bottom,
    '--media-desktop-margin-right': margin.value?.desktop?.right,
    '--media-tablet-margin-top': margin.value?.tablet?.top,
    '--media-tablet-margin-left': margin.value?.tablet?.left,
    '--media-tablet-margin-bottom': margin.value?.tablet?.bottom,
    '--media-tablet-margin-right': margin.value?.tablet?.right,
    '--media-mobile-margin-top': margin.value?.mobile?.top,
    '--media-mobile-margin-left': margin.value?.mobile?.left,
    '--media-mobile-margin-bottom': margin.value?.mobile?.bottom,
    '--media-mobile-margin-right': margin.value?.mobile?.right,

    '--media-desktop-padding-top': padding.value?.desktop?.top,
    '--media-desktop-padding-left': padding.value?.desktop?.left,
    '--media-desktop-padding-bottom': padding.value?.desktop?.bottom,
    '--media-desktop-padding-right': padding.value?.desktop?.right,
    '--media-tablet-padding-top': padding.value?.tablet?.top,
    '--media-tablet-padding-left': padding.value?.tablet?.left,
    '--media-tablet-padding-bottom': padding.value?.tablet?.bottom,
    '--media-tablet-padding-right': padding.value?.tablet?.right,
    '--media-mobile-padding-top': padding.value?.mobile?.top,
    '--media-mobile-padding-left': padding.value?.mobile?.left,
    '--media-mobile-padding-bottom': padding.value?.mobile?.bottom,
    '--media-mobile-padding-right': padding.value?.mobile?.right,
  }
})
</script>

<style lang="postcss" scoped>
.tw-media {
  /* PADDING */
  padding-top: var(--media-mobile-padding-top, 0px);
  padding-left: var(--media-mobile-padding-left, 0px);
  padding-bottom: var(--media-mobile-padding-bottom, 0px);
  padding-right: var(--media-mobile-padding-right, 0px);
  
  /* MARGIN */
  margin-top: var(--media-mobile-margin-top, 0px);
  margin-left: var(--media-mobile-margin-left, 0px);
  margin-bottom: var(--media-mobile-margin-bottom, 0px);
  margin-right: var(--media-mobile-margin-right, 0px);
  
  @media (min-width: 768px) {
    padding-top: var(--media-tablet-padding-top, 0px);
    padding-left: var(--media-tablet-padding-left, 0px);
    padding-bottom: var(--media-tablet-padding-bottom, 0px);
    padding-right: var(--media-tablet-padding-right, 0px);
    
    margin-top: var(--media-tablet-margin-top, 0px);
    margin-left: var(--media-tablet-margin-left, 0px);
    margin-bottom: var(--media-tablet-margin-bottom, 0px);
    margin-right: var(--media-tablet-margin-right, 0px);
  }

  @media (min-width: 1024px) {
    padding-top: var(--media-desktop-padding-top, 0px);
    padding-left: var(--media-desktop-padding-left, 0px);
    padding-bottom: var(--media-desktop-padding-bottom, 0px);
    padding-right: var(--media-desktop-padding-right, 0px);
    
    margin-top: var(--media-desktop-margin-top, 0px);
    margin-left: var(--media-desktop-margin-left, 0px);
    margin-bottom: var(--media-desktop-margin-bottom, 0px);
    margin-right: var(--media-desktop-margin-right, 0px);
  }
}

.desktop {
  .tw-media {
    padding-top: var(--media-desktop-padding-top, 0px);
    padding-left: var(--media-desktop-padding-left, 0px);
    padding-bottom: var(--media-desktop-padding-bottom, 0px);
    padding-right: var(--media-desktop-padding-right, 0px);
    
    margin-top: var(--media-desktop-margin-top, 0px);
    margin-left: var(--media-desktop-margin-left, 0px);
    margin-bottom: var(--media-desktop-margin-bottom, 0px);
    margin-right: var(--media-desktop-margin-right, 0px);
  }
}

.tablet {
  .tw-media {
    padding-top: var(--media-tablet-padding-top, 0px);
    padding-left: var(--media-tablet-padding-left, 0px);
    padding-bottom: var(--media-tablet-padding-bottom, 0px);
    padding-right: var(--media-tablet-padding-right, 0px);
    
    margin-top: var(--media-tablet-margin-top, 0px);
    margin-left: var(--media-tablet-margin-left, 0px);
    margin-bottom: var(--media-tablet-margin-bottom, 0px);
    margin-right: var(--media-tablet-margin-right, 0px);
  }
}

.mobile {
  .tw-media {
    padding-top: var(--media-mobile-padding-top, 0px);
    padding-left: var(--media-mobile-padding-left, 0px);
    padding-bottom: var(--media-mobile-padding-bottom, 0px);
    padding-right: var(--media-mobile-padding-right, 0px);
    
    margin-top: var(--media-mobile-margin-top, 0px);
    margin-left: var(--media-mobile-margin-left, 0px);
    margin-bottom: var(--media-mobile-margin-bottom, 0px);
    margin-right: var(--media-mobile-margin-right, 0px);
  }
}
</style>
